import React, {Fragment} from "react";
import {Popover, Transition} from '@headlessui/react'
import {
    Bars3Icon,
    DocumentChartBarIcon,
    LanguageIcon,
    MoonIcon,
    Squares2X2Icon,
    SunIcon,
    TagIcon,
    UserGroupIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import Image from "next/image";
import Link from "@components/elements/DocumentLink";
import {withAssetVersion} from "@utils/assets";


interface IMenuItem {
    name: string,
    description: string,
    href: string,
    icon: any,
}

const menuItems: IMenuItem[] = [
    {
        name: 'All plugins',
        description: 'Browse all plugins in the Marketplace.',
        href: '/plugins',
        icon: Squares2X2Icon,
    },
    {
        name: 'Tags',
        description: 'Explore plugin tags and niches.',
        href: '/tags',
        icon: TagIcon,
    },
    {
        name: 'Languages',
        description: 'Browse plugins by language packs.',
        href: '/languages',
        icon: LanguageIcon,
    },
    {
        name: 'Authors',
        description: 'Browse plugin authors and their work.',
        href: '/authors',
        icon: UserGroupIcon,
    },
    // TODO: Add this back when the page is ready
    // {
    //     name: 'Feedback & suggestions',
    //     description: 'Let us know what you think about the Marketplace.',
    //     href: '/feedback',
    //     icon: CursorArrowRaysIcon,
    // },
    {
        name: 'About us',
        description: 'Learn more about us and our mission.',
        href: '/about',
        icon: DocumentChartBarIcon,
    }
]

interface INavbarProps {
    className?: string
}

interface INavbarState {
}

function toggleTheme() {
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    const nextMode = isDark ? 'light' : 'dark';

    if (nextMode === 'dark') {
        root.classList.add('dark');
        root.dataset.theme = 'dark';
    } else {
        root.classList.remove('dark');
        root.dataset.theme = 'light';
    }

    try {
        window.localStorage.setItem('color-mode', nextMode);
    } catch {
        // Ignore write errors (e.g., disabled storage)
    }
}

function ThemeToggleButton(props: {className?: string}) {
    return (
        <button
            type="button"
            onClick={toggleTheme}
            className={props.className}
            title="Toggle theme"
            aria-label="Toggle theme"
        >
            <MoonIcon className="h-6 w-6 text-gray-500 dark:hidden" aria-hidden="true"/>
            <SunIcon className="hidden h-6 w-6 text-gray-200 dark:block" aria-hidden="true"/>
        </button>
    );
}

export default class Navbar extends React.Component<INavbarProps, INavbarState> {
    render() {
        const logoSrc = withAssetVersion("/images/logo/header-logo.png");
        const containerClassName = [
            this.props.className,
            "sticky top-0 z-30",
            "border-b border-gray-200 dark:border-gray-700",
            "bg-white/80 dark:bg-gray-900/70",
            "backdrop-blur"
        ].filter(Boolean).join(" ");
        return (
            <Popover className={containerClassName}>
                <div className="flex items-center justify-between px-4 py-4 sm:px-6 md:justify-start md:gap-8">
                    <div className="flex justify-start lg:w-0 lg:flex-1">
                        <Link href="/" className="flex items-center">
                            <span className="sr-only company-name">{process.env.NEXT_PUBLIC_WEBSITE_NAME}</span>
                            <Image
                                className="h-8 w-auto sm:h-10 rounded-full"
                                src={logoSrc}
                                alt={process.env.NEXT_PUBLIC_WEBSITE_NAME + " logo"}
                                width={40}
                                height={40}
                                priority
                            />
                        </Link>
                    </div>
                    <div className="-my-2 -mr-2 md:hidden">
                        <Popover.Button
                            className="inline-flex items-center justify-center rounded-md  p-2
                                        bg-white text-gray-400
                                        hover:bg-gray-100 hover:text-gray-500
                                        focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500
                                        dark:text-white dark:bg-gray-800
                                        dark:hover:bg-gray-700 dark:hover:text-white
                                        dark:focus:ring-indigo-600
                             ">
                            <span className="sr-only">Open menu</span>
                            <Bars3Icon className="h-6 w-6" aria-hidden="true"/>
                        </Popover.Button>
                    </div>
                    <div className="hidden items-center justify-end md:flex md:flex-1 lg:w-0 gap-2">
                        {menuItems.map((item) => (
                            <Link
                                key={item.name}
                                href={item.href}
                                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                                <item.icon className="h-5 w-5 text-gray-500 dark:text-gray-300" aria-hidden="true"/>
                                <span>{item.name}</span>
                            </Link>
                        ))}
                        <ThemeToggleButton
                            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-600"
                        />
                    </div>
                </div>

                <Transition
                    as={Fragment}
                    enter="duration-200 ease-out"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="duration-100 ease-in"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <Popover.Panel focus
                                   className="absolute inset-x-0 top-0 origin-top-right transform p-2 transition md:hidden z-30">
                        <div
                            className="divide-y rounded-2xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                            <div className="px-5 pt-5 pb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Image
                                            className="h-9 w-9 rounded-full"
                                            src={logoSrc}
                                            alt={process.env.NEXT_PUBLIC_WEBSITE_NAME + " logo"}
                                            width={40}
                                            height={40}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2 -mr-2">
                                        <ThemeToggleButton
                                            className="inline-flex items-center justify-center rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        />
                                        <Popover.Button
                                            className="inline-flex items-center justify-center rounded-md p-2 focus:outline-none
                                                        bg-white text-gray-400
                                                        hover:bg-gray-100 hover:text-gray-500
                                                        focus:ring-2 focus:ring-inset focus:ring-indigo-500
                                                        dark:text-white dark:bg-gray-800
                                            ">
                                            <span className="sr-only">Close menu</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
                                        </Popover.Button>
                                    </div>
                                </div>
                            </div>
                            <div className="py-6 px-5">
                                <div className="grid grid-cols-1 gap-4">
                                    {menuItems.map((item) => (
                                        <Link
                                            key={item.name}
                                            href={item.href}
                                            className="-m-3 flex items-start rounded-xl p-3 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <item.icon className="flex-shrink-0 h-6 w-6 text-indigo-600 dark:text-indigo-300"
                                                       aria-hidden="true"/>
                                            <span
                                                className="ml-3 text-base font-medium
                                                            text-gray-900
                                                            dark:text-white
                                                ">{item.name}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Popover.Panel>
                </Transition>
            </Popover>
        )
    }
}
